import { useMutation } from '@tanstack/react-query'
import React from 'react'

import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useInvalidatePanelQueries } from '@/hooks/useInvalidatePanelQueries'
import useNotyf from '@/hooks/useNotyf'
import panelService from '@/lib/services/panelService'
import { CompanyProfileItemPanel } from '@/types/responses/companies'

interface Props {
  onHide: () => void
  panel: CompanyProfileItemPanel
}

const DeclineInviteModal: React.FC<Props> = ({ onHide, panel }) => {
  const notyf = useNotyf()
  const invalidatePanelsQuery = useInvalidatePanelQueries()

  const declinePanelInviteMutation = useMutation({
    mutationFn: () => panelService.declinePanelInvite(panel.id),
    onSuccess: async () => {
      await invalidatePanelsQuery()
      notyf.success(
        `You have declined the relationship invite from ${panel.fromCompany.presentationName}`,
      )
      onHide()
    },
    onError: () => {
      notyf.error('Failed to decline relationship invite')
    },
  })

  return (
    <Dialog open onHide={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decline relationship invitation</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <p className="mb-2">
            <strong>{panel.fromCompany.presentationName}</strong> has invited
            you to join their professional network.
          </p>

          <p className="mb-0">
            If you decline their invite, you will not be able to refer clients
            between each other.
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>

          <ActionButton
            variant="destructive"
            onClick={() => declinePanelInviteMutation.mutate()}
            isProcessing={declinePanelInviteMutation.isPending}
            isProcessingText="Declining invite"
          >
            Decline invitation
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DeclineInviteModal
